import { GroupByMode, newTaskSuffix } from '@griply/types';
import {
  createDefaultSchedule,
  getNewTaskPrefill,
  onTypeReorder,
} from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import {
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list/types';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useTypeTree } from '../../hooks/use-type-tree';
import { GroupedListProps } from '../../types';
import { TreeTaskCard } from '../tree-task-card';
import { TypeHeader } from '../type-header';
import * as Styled from './type-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

export const TypeGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTypeTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onTypeReorder({ updatedItems });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.Type,
            groupSectionId,
            groupSetting: groupSettings,
            today,
            defaultNewTaskDate,
          });
          return (
            <NewTaskRow
              date={prefill.startDate}
              schedules={
                prefill.isHabit ? [createDefaultSchedule(today)] : undefined
              }
              startTime={null}
              duration={null}
              goal={prefill.goalId}
              lifeArea={prefill.lifeAreaId}
              tags={prefill.tagIds}
            />
          );
        }

        // Section Header (Type)
        if (type === TaskListItemTypes.Type) {
          return <TypeHeader item={itemInstance} denseFormat={denseFormat} />;
        }

        return null;
      },
    [groupSettings, today, defaultNewTaskDate, denseFormat],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
